"use client"

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Download, Trash2 } from 'lucide-react';
import {
  Button,
  ConfirmationDialog,
  DatePicker,
  DateTimeDisplay,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@t-one-internal/design-system';
import type { EmployeeDetails } from '../../../stores/employeeProfileStore';
import { useLocationStore } from '../../../stores/locationStore';
import { useWorkroleStore } from '../../../stores/workroleStore';
import { domainAvailabilityService, type DomainAvailabilityResponse } from '../../../services/domainAvailabilityService';
import apiClient, { getAuthToken, API_BASE_URL } from '../../../api/apiClient';
import { SmartManagerDropdown } from '../../../components/organization/SmartManagerDropdown';
import { SmartHRDropdown } from '../../../components/organization/SmartHRDropdown';
import { SmartJobTitleDropdown } from '../../../components/organization/SmartJobTitleDropdown';
import { OrgUnitSelector } from '../../../components/organization/OrgUnitSelector';
import { showToast } from '@t-one-internal/design-system/lib/components/ui/sonner.tsx';

// --- API data types ---

interface JobTitleItem {
  job_title_id: number | string;
  name: string;
  scope_instance_id?: number | string | null;
}

// --- Props ---

interface JobDetailsSectionProps {
  employee: EmployeeDetails;
  onUpdate?: (updates: Partial<EmployeeDetails>) => void;
  readOnly?: boolean;
  isEditing?: boolean;
  hideTitle?: boolean;
  onHierarchyPreview?: (path: any[]) => void;
  onValidationChange?: (hasErrors: boolean) => void;
}

// --- Dropdown options ---

const eeocCategories = [
  'Senior Level Officials',
  'First/mid-level officials and managers',
  'Professionals',
  'Technicians',
  'Sales Workers',
  'Administrative support workers',
  'Craft workers',
  'Operatives',
  'Laborers and helpers',
  'Service workers',
];

// Helper function to extract numeric ID as STRING to avoid BIGINT precision loss
const extractNumericId = (value: string | number | undefined): string => {
  if (!value) return '';
  const str = value.toString();
  const match = str.match(/\d+/);
  return match ? match[0] : str;
};

const traineryContacts = ['Yes', 'No'];
const employmentStatuses = ['Active', 'Inactive'];
const employmentTypes = ['Full-Time', 'Part-Time', 'Contract', 'Temporary', 'Intern'];

// --- Valid file types for position description uploads ---

const VALID_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/rtf',
  'application/rtf',
];
const VALID_DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf', 'odt'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

// --- Component ---

export const JobDetailsSection: React.FC<JobDetailsSectionProps> = ({
  employee,
  onUpdate,
  readOnly = false,
  isEditing: externalIsEditing,
  hideTitle = false,
  onHierarchyPreview,
  onValidationChange,
}) => {
  // --- Edit state ---
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = setInternalIsEditing;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  // --- Position description state ---
  const [isUploadingPositionDesc, setIsUploadingPositionDesc] = useState(false);
  const [isDownloadingPositionDesc, setIsDownloadingPositionDesc] = useState(false);
  const [isDeletingPositionDesc, setIsDeletingPositionDesc] = useState(false);
  const [positionDescError, setPositionDescError] = useState<string | null>(null);
  const [showDeletePositionDescDialog, setShowDeletePositionDescDialog] = useState(false);


  // --- Employee ID availability checking ---
  const [employeeIdError, setEmployeeIdError] = useState<string | null>(null);
  const [isCheckingEmployeeId, setIsCheckingEmployeeId] = useState(false);
  const employeeIdCheckTimeoutRef = useRef<number | null>(null);

  // --- Track previous external editing state for auto-save transitions ---
  const prevExternalIsEditing = useRef(externalIsEditing);

  // --- Controlled form state for editable fields ---
  const [empIdValue, setEmpIdValue] = useState<string>('');
  const [jobCodeValue, setJobCodeValue] = useState<string>('');
  const [jobGradeValue, setJobGradeValue] = useState<string>('');
  const [standardHoursValue, setStandardHoursValue] = useState<string>('');
  const [eeocCategoryValue, setEeocCategoryValue] = useState<string>('');
  const [trainerContactValue, setTrainerContactValue] = useState<string>('');
  const [employmentStatusValue, setEmploymentStatusValue] = useState<string>('');
  const [employmentTypeValue, setEmploymentTypeValue] = useState<string>('');

  // --- Manager / Secondary Manager / Unit state ---
  const [managerValue, setManagerValue] = useState<string>('');
  const [secondaryManagerValue, setSecondaryManagerValue] = useState<string>('');
  const [hrPartnerValue, setHrPartnerValue] = useState<string>('');
  const [primaryOrgUnitValue, setPrimaryOrgUnitValue] = useState<string>('');

  // --- Location / Work Role / Job Title state ---
  const [locationValue, setLocationValue] = useState<string>('');
  const [workRoleValue, setWorkRoleValue] = useState<string>('');
  const [jobTitleIdValue, setJobTitleIdValue] = useState<string>('');
  const [jobTitleNameValue, setJobTitleNameValue] = useState<string>('');

  // --- Use stores for locations and work roles ---
  const { locations, fetchLocations, isLoading: isLoadingLocations } = useLocationStore();
  const { workroles, fetchWorkroles, isLoading: isLoadingWorkRoles } = useWorkroleStore();

  // --- Resolved job title for read-only display ---
  const [resolvedJobTitle, setResolvedJobTitle] = useState<string>('-');

  // --- Resolved manager names and avatars for read-only display ---
  const [resolvedManagerName, setResolvedManagerName] = useState<string>('-');
  const [resolvedManagerAvatar, setResolvedManagerAvatar] = useState<string | null>(null);
  const [resolvedSecondaryManagerName, setResolvedSecondaryManagerName] = useState<string>('-');
  const [resolvedSecondaryManagerAvatar, setResolvedSecondaryManagerAvatar] = useState<string | null>(null);
  const [resolvedHrPartnerName, setResolvedHrPartnerName] = useState<string>('-');
  const [resolvedHrPartnerAvatar, setResolvedHrPartnerAvatar] = useState<string | null>(null);

  // --- Org instances for hierarchy preview ---
  const [orgInstances, setOrgInstances] = useState<Array<{ scope_instance_id: string; name: string; parent_instance_id: string | null; level_name?: string }>>([]);

  // Fetch org instances for hierarchy path building
  useEffect(() => {
    const fetchInstances = async () => {
      try {
        const { data } = await apiClient.get<Array<{ scope_instance_id: string; name: string; parent_instance_id: string | null; level_name?: string }>>('/admin/scope-instances');
        setOrgInstances(data || []);
      } catch (e) {
        console.error('Failed to fetch scope instances for hierarchy:', e);
      }
    };
    fetchInstances();
  }, []);

  // Fetch locations and work roles from stores
  useEffect(() => {
    fetchLocations();
    fetchWorkroles();
  }, [fetchLocations, fetchWorkroles]);

  // Resolve job title name from ID for read-only display
  useEffect(() => {
    const jobTitle = employee.jobTitle?.trim();
    if (jobTitle) {
      setResolvedJobTitle(jobTitle);
      return;
    }
    const jobTitleId = employee.jobTitleId ? String(employee.jobTitleId).trim() : '';
    if (!jobTitleId) {
      setResolvedJobTitle('-');
      return;
    }
    let cancelled = false;
    const resolve = async () => {
      try {
        const { data } = await apiClient.get<{ data: JobTitleItem[] }>('/job-title/');
        if (cancelled) return;
        const match = (data?.data || []).find((jt) => String(jt.job_title_id) === jobTitleId);
        setResolvedJobTitle(match?.name ?? '-');
      } catch {
        if (!cancelled) setResolvedJobTitle('-');
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [employee.jobTitle, employee.jobTitleId]);

  // Resolve manager names from IDs for read-only display
  useEffect(() => {
    const resolveManagerName = async (managerId: string | undefined): Promise<string> => {
      if (!managerId || managerId === '-' || managerId.trim() === '') return '-';

      try {
        // Fetch user details from the admin endpoint
        const { data } = await apiClient.get<{ user?: any }>(`/admin/users/${managerId}`);
        const user = data.user || data;
        const firstName = user.first_name || user.fname || '';
        const lastName = user.last_name || user.lname || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || managerId; // Fallback to ID if name not found
      } catch (error) {
        console.error(`Failed to resolve manager name for ${managerId}:`, error);
        return managerId; // Fallback to showing the ID if fetch fails
      }
    };

    // Only resolve names when not in edit mode
    if (!isEditing) {
      // Resolve manager
      if (employee.manager && employee.manager !== '-') {
        resolveManagerName(employee.manager).then(setResolvedManagerName);
      } else {
        setResolvedManagerName('-');
      }

      // Resolve secondary manager
      if (employee.secondaryManager && employee.secondaryManager !== '-') {
        resolveManagerName(employee.secondaryManager).then(setResolvedSecondaryManagerName);
      } else {
        setResolvedSecondaryManagerName('-');
      }

      // Resolve HR partner
      if (employee.hrPartner && employee.hrPartner !== '-') {
        resolveManagerName(employee.hrPartner).then(setResolvedHrPartnerName);
      } else {
        setResolvedHrPartnerName('-');
      }
    }
  }, [employee.manager, employee.secondaryManager, employee.hrPartner, isEditing]);

  // Build and emit hierarchy path when org unit changes
  useEffect(() => {
    if (onHierarchyPreview && primaryOrgUnitValue && orgInstances.length > 0) {
      const path: Array<{ id: string; name: string; level: string }> = [];
      let currentId: string | null = primaryOrgUnitValue;

      while (currentId) {
        const node = orgInstances.find(inst => inst.scope_instance_id === currentId);
        if (!node) break;
        path.unshift({
          id: node.scope_instance_id,
          name: node.name,
          level: node.level_name || 'Unit',
        });
        currentId = node.parent_instance_id;
      }

      onHierarchyPreview(path);
    }
  }, [primaryOrgUnitValue, orgInstances, onHierarchyPreview]);

  // --- Date state ---
  const [openCalendar, setOpenCalendar] = useState<'hire' | 'start' | 'position' | null>(null);
  const [pendingDates, setPendingDates] = useState<{
    hireDate?: string;
    startDate?: string;
    positionDate?: string;
  }>({});
  const [dateErrors, setDateErrors] = useState<{
    startDate?: string;
    positionDate?: string;
  }>({});

  // --- Validation warning dialog ---
  const [validationWarning, setValidationWarning] = useState<{ show: boolean; message: string }>({
    show: false,
    message: '',
  });

  // --- Original values for change detection ---
  const [originalValues, setOriginalValues] = useState<Partial<EmployeeDetails> | null>(null);

  // --- Initialize controlled state when employee data changes (only outside edit mode) ---
  useEffect(() => {
    if (!employee || isEditing) return;

    setEmpIdValue(employee.employee_id || '');
    setJobCodeValue(employee.jobCode || '');
    setJobGradeValue(employee.grade || '');
    setStandardHoursValue(employee.standardHours || '40');
    setEeocCategoryValue(employee.eeocCategory || '');
    setTrainerContactValue(employee.trainerContact ? 'Yes' : 'No');
    setEmploymentStatusValue(employee.status || 'Active');
    setEmploymentTypeValue(employee.employmentType || 'Full-Time');
    setManagerValue(extractNumericId(employee.manager) || '');
    setSecondaryManagerValue(extractNumericId(employee.secondaryManager) || '');
    setHrPartnerValue(extractNumericId(employee.hrPartner) || '');
    setPrimaryOrgUnitValue(employee.primaryOrgUnit || '');
    setJobTitleIdValue((employee as any).jobTitleId ?? '');
    setJobTitleNameValue(employee.jobTitle ?? '');

    // Initialize location: match by ID or name
    const locationSource = employee.location || employee.branch;
    if (locationSource && locations.length > 0) {
      const matchById = locations.find(l => String(l.id) === String(locationSource));
      const matchByName = locations.find(l => l.name.toLowerCase() === locationSource.toLowerCase());
      setLocationValue(matchById ? String(matchById.id) : matchByName ? String(matchByName.id) : locationSource);
    } else {
      setLocationValue(locationSource || '');
    }

    // Initialize work role: match by name to get role_id
    if (employee.workRole && workroles.length > 0) {
      const matchByName = workroles.find(r => r.name === employee.workRole);
      setWorkRoleValue(matchByName ? matchByName.role_id : employee.workRole);
    } else {
      setWorkRoleValue(employee.workRole || '');
    }

    setPendingDates({
      hireDate: employee.hireDateISO || undefined,
      startDate: employee.startDateISO || undefined,
      positionDate: employee.positionDateISO || undefined,
    });
  }, [employee, isEditing, locations, workroles]);

  // --- Date validation ---

  const handleDateChange = (
    field: 'hireDate' | 'startDate' | 'positionDate',
    isoString: string | undefined,
  ): void => {
    setPendingDates((prev) => ({ ...prev, [field]: isoString }));

    const currentHireDate = field === 'hireDate' ? isoString : (pendingDates.hireDate ?? employee.hireDateISO);
    const currentStartDate = field === 'startDate' ? isoString : (pendingDates.startDate ?? employee.startDateISO);
    const currentPositionDate = field === 'positionDate' ? isoString : (pendingDates.positionDate ?? employee.positionDateISO);

    setDateErrors((prev) => {
      const newErrors = { ...prev };

      // Start Date must be on or after Hire Date
      if (currentStartDate && currentHireDate) {
        if (new Date(currentStartDate) < new Date(currentHireDate)) {
          newErrors.startDate = 'Start Date cannot be before Hire Date.';
        } else {
          delete newErrors.startDate;
        }
      } else {
        delete newErrors.startDate;
      }

      // Position Date must be on or after Start Date
      if (currentPositionDate && currentStartDate) {
        if (new Date(currentPositionDate) < new Date(currentStartDate)) {
          newErrors.positionDate = 'Position Date cannot be before Start Date.';
        } else {
          delete newErrors.positionDate;
        }
      } else {
        delete newErrors.positionDate;
      }

      return newErrors;
    });
  };

  // --- Employee ID availability check (debounced) ---

  const checkEmployeeIdAvailability = useCallback(
    async (employeeId: string) => {
      if (!employeeId || employeeId.trim().length === 0) {
        setEmployeeIdError(null);
        return;
      }

      // Skip check if value matches original
      if (originalValues?.employee_id && employeeId.trim() === (originalValues.employee_id || '').trim()) {
        setEmployeeIdError(null);
        return;
      }

      setIsCheckingEmployeeId(true);
      try {
        const response: DomainAvailabilityResponse = await domainAvailabilityService.checkEmployeeId(employeeId);
        const hasConflict = response.conflicts.employee_id === true;
        const message = response.details.employee_id || '';

        if (hasConflict && message.trim().length > 0) {
          setEmployeeIdError(message);
        } else {
          setEmployeeIdError(null);
        }
      } catch (error) {
        console.error('Error checking employee ID availability:', error);
        setEmployeeIdError(null);
      } finally {
        setIsCheckingEmployeeId(false);
      }
    },
    [originalValues],
  );

  useEffect(() => {
    if (!isEditing) {
      setEmployeeIdError(null);
      return;
    }

    if (employeeIdCheckTimeoutRef.current) {
      clearTimeout(employeeIdCheckTimeoutRef.current);
    }

    if (empIdValue && empIdValue.trim().length > 0) {
      employeeIdCheckTimeoutRef.current = window.setTimeout(() => {
        checkEmployeeIdAvailability(empIdValue);
      }, 700);
    } else {
      setEmployeeIdError(null);
    }

    return () => {
      if (employeeIdCheckTimeoutRef.current) {
        clearTimeout(employeeIdCheckTimeoutRef.current);
      }
    };
  }, [empIdValue, isEditing, checkEmployeeIdAvailability]);

  // --- Collect changed fields ---

  const collectFormData = (): Partial<EmployeeDetails> => {
    if (!originalValues) return {};

    const formData: Record<string, unknown> = {};

    const hasChanged = (field: keyof EmployeeDetails, newValue: unknown): boolean => {
      const original = originalValues[field];
      if (newValue === undefined || newValue === null || newValue === '') {
        return original !== undefined && original !== null && original !== '';
      }
      return String(newValue) !== String(original);
    };

    // Editable text fields
    if (hasChanged('employee_id', empIdValue)) formData.employee_id = empIdValue;
    if (hasChanged('jobCode', jobCodeValue)) formData.jobCode = jobCodeValue;
    if (hasChanged('grade', jobGradeValue)) formData.grade = jobGradeValue;
    if (hasChanged('standardHours', standardHoursValue)) formData.standardHours = standardHoursValue;

    // Editable dropdown fields
    if (hasChanged('eeocCategory', eeocCategoryValue)) formData.eeocCategory = eeocCategoryValue;
    if (hasChanged('status', employmentStatusValue)) formData.status = employmentStatusValue;
    if (hasChanged('employmentType', employmentTypeValue)) formData.employmentType = employmentTypeValue;

    const trainerContactBool = trainerContactValue === 'Yes';
    if (hasChanged('trainerContact', trainerContactBool)) formData.trainerContact = trainerContactBool;

    // Manager / Secondary Manager / HR Partner / Unit
    if (hasChanged('manager', managerValue || undefined)) formData.manager = managerValue || undefined;
    if (hasChanged('secondaryManager', secondaryManagerValue || undefined)) formData.secondaryManager = secondaryManagerValue || undefined;
    if (hasChanged('hrPartner', hrPartnerValue || undefined)) formData.hrPartner = hrPartnerValue || undefined;
    if (hasChanged('primaryOrgUnit', primaryOrgUnitValue || undefined)) formData.primaryOrgUnit = primaryOrgUnitValue || undefined;

    // Location
    if (hasChanged('location', locationValue || undefined)) formData.location = locationValue || undefined;

    // Work Role
    if (hasChanged('workRole', workRoleValue || undefined)) {
      formData.workRole = workRoleValue || undefined;
      (formData as any).work_role = workRoleValue || undefined;
    }

    // Job Title (via job_title_id)
    if (hasChanged('jobTitleId', jobTitleIdValue || undefined)) {
      formData.jobTitleId = jobTitleIdValue || undefined;
      formData.jobTitle = jobTitleNameValue || undefined;
    }

    // Date fields
    const hasDateChanged = (
      field: 'hireDateISO' | 'startDateISO' | 'positionDateISO',
      newValue: string | undefined,
    ): boolean => {
      const original = originalValues[field];
      return (newValue || undefined) !== (original || undefined);
    };

    if (hasDateChanged('hireDateISO', pendingDates.hireDate)) {
      formData.hireDateISO = pendingDates.hireDate;
      formData.hireDate = pendingDates.hireDate ? DateTimeDisplay.toStandardDate(pendingDates.hireDate) : null;
    }
    if (hasDateChanged('startDateISO', pendingDates.startDate)) {
      formData.startDateISO = pendingDates.startDate;
      formData.startDate = pendingDates.startDate ? DateTimeDisplay.toStandardDate(pendingDates.startDate) : null;
    }
    if (hasDateChanged('positionDateISO', pendingDates.positionDate)) {
      formData.positionDateISO = pendingDates.positionDate;
      formData.positionDate = pendingDates.positionDate
        ? DateTimeDisplay.toStandardDate(pendingDates.positionDate)
        : null;
    }

    return formData as Partial<EmployeeDetails>;
  };

  // --- Save handlers ---

  const handleSave = (): void => {
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = async (skipEditingToggle = false): Promise<void> => {
    setIsSaving(true);
    try {
      // Validate dates
      const finalStartDate = pendingDates.startDate ?? employee.startDateISO;
      const finalHireDate = pendingDates.hireDate ?? employee.hireDateISO;
      const finalPositionDate = pendingDates.positionDate ?? employee.positionDateISO;

      let hasError = false;
      const newErrors: { startDate?: string; positionDate?: string } = {};

      if (!finalStartDate || finalStartDate.trim() === '') {
        newErrors.startDate = 'Start Date is required.';
        hasError = true;
      }

      if (finalHireDate && finalStartDate && new Date(finalStartDate) < new Date(finalHireDate)) {
        newErrors.startDate = 'Start Date cannot be before Hire Date.';
        hasError = true;
      }

      if (finalStartDate && finalPositionDate && new Date(finalPositionDate) < new Date(finalStartDate)) {
        newErrors.positionDate = 'Position Date cannot be before Start Date.';
        hasError = true;
      }

      if (hasError) {
        setDateErrors(newErrors);
        setIsSaving(false);
        setShowConfirmDialog(false);
        return;
      }

      const formData = collectFormData();
      if (onUpdate && Object.keys(formData).length > 0) {
        await onUpdate(formData);
      }

      if (!skipEditingToggle) setIsEditing(false);
      setShowConfirmDialog(false);
      setOriginalValues(null);
    } catch (error) {
      console.error('Error saving job details:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (): void => {
    setIsEditing(false);
    setOriginalValues(null);
    setPendingDates({});
  };

  // --- External edit mode: auto-save when toggling from editing to not editing ---

  const handleExternalSave = async (): Promise<void> => {
    try {
      const formData = collectFormData();
      if (onUpdate && Object.keys(formData).length > 0) {
        await onUpdate(formData);
      }
      setOriginalValues(null);
    } catch (error) {
      console.error('Error saving job details:', error);
    }
  };

  useEffect(() => {
    // When external editing starts, snapshot original values
    if (externalIsEditing && !prevExternalIsEditing.current) {
      setOriginalValues({
        grade: employee.grade,
        jobCode: employee.jobCode,
        employee_id: employee.employee_id,
        eeocCategory: employee.eeocCategory,
        standardHours: employee.standardHours,
        trainerContact: employee.trainerContact,
        status: employee.status,
        employmentType: employee.employmentType,
        hireDateISO: employee.hireDateISO,
        startDateISO: employee.startDateISO,
        positionDateISO: employee.positionDateISO,
        manager: employee.manager,
        secondaryManager: employee.secondaryManager,
        hrPartner: employee.hrPartner,
        primaryOrgUnit: employee.primaryOrgUnit,
        location: employee.location,
        workRole: employee.workRole,
        jobTitle: employee.jobTitle,
        jobTitleId: employee.jobTitleId,
      });
      setDateErrors({});
      setEmployeeIdError(null);
    }

    // When external editing ends, auto-save
    if (!externalIsEditing && prevExternalIsEditing.current) {
      if (originalValues) {
        handleExternalSave();
      }
    }

    prevExternalIsEditing.current = externalIsEditing;
  }, [externalIsEditing, employee]);

  // --- Position description helpers (use apiClient directly) ---

  const handlePositionDescUpload = async (): Promise<void> => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const isValidType =
        VALID_DOCUMENT_MIME_TYPES.includes(file.type) ||
        (fileExtension !== undefined && VALID_DOCUMENT_EXTENSIONS.includes(fileExtension));
      const isValidSize = file.size <= MAX_FILE_SIZE;

      if (!isValidType) {
        showToast.error('Unsupported file format', {
          description: 'Only .pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt, .rtf, and .odt files are supported.',
        });
        return;
      }
      if (!isValidSize) {
        showToast.error('File too large', {
          description: 'Maximum file size is 10 MB.',
        });
        return;
      }

      try {
        setIsUploadingPositionDesc(true);
        setPositionDescError(null);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('filename', file.name);

        // Use fetch instead of axios to handle BigInt attachment_id safely
        const token = getAuthToken();
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        // Add impersonation token if active
        const impersonationDataStr = localStorage.getItem('impersonation_data');
        if (impersonationDataStr) {
          try {
            const impData = JSON.parse(impersonationDataStr);
            if (impData.sessionToken) headers['X-Impersonation-Token'] = impData.sessionToken;
          } catch (_e) { /* ignore */ }
        }

        const fetchResponse = await fetch(`${API_BASE_URL}/attachments`, {
          method: 'POST',
          headers,
          body: formData,
        });

        if (!fetchResponse.ok) {
          const errBody = await fetchResponse.json().catch(() => ({ error: `HTTP ${fetchResponse.status}` }));
          throw new Error(errBody.error || `Failed to upload file: ${fetchResponse.statusText}`);
        }

        // Parse as text first, then quote attachment_id to prevent BigInt precision loss
        const raw = await fetchResponse.text();
        const safeRaw = raw.replace(/("attachment_id"\s*:\s*)(\d+)/, '$1"$2"');
        const uploadResult = JSON.parse(safeRaw);

        if (uploadResult.attachment_id) {
          if (onUpdate) {
            await onUpdate({
              positionDescription: String(uploadResult.attachment_id),
              positionDescriptionFileName: file.name,
            });
          }
          showToast.success('Job description uploaded successfully');
        } else {
          throw new Error('No attachment ID returned from upload');
        }
      } catch (error: any) {
        console.error('Failed to upload file:', error);
        setPositionDescError(error.message || 'Failed to upload file');
      } finally {
        setIsUploadingPositionDesc(false);
      }
    };
    input.click();
  };

  const handlePositionDescDownload = async (): Promise<void> => {
    try {
      setIsDownloadingPositionDesc(true);
      setPositionDescError(null);

      const response = await apiClient.get<{ temporary_url: string }>(
        `/attachments/${employee.positionDescription}/url?expiry=3600`,
      );

      if (response.data.temporary_url) {
        const link = document.createElement('a');
        link.href = response.data.temporary_url;
        link.download = employee.positionDescriptionFileName || 'job-description';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('No download URL returned');
      }
    } catch (error: any) {
      console.error('Failed to download file:', error);
      setPositionDescError(error.message || 'Failed to download file');
    } finally {
      setIsDownloadingPositionDesc(false);
    }
  };

  const handlePositionDescDelete = async (): Promise<void> => {
    try {
      setIsDeletingPositionDesc(true);
      setPositionDescError(null);

      // Attempt to delete the attachment from storage
      try {
        await apiClient.delete(`/attachments/${employee.positionDescription}`);
      } catch (attachmentError: any) {
        console.warn('Failed to delete attachment from storage (continuing anyway):', attachmentError?.message);
      }

      // Clear the reference in the user record
      if (onUpdate) {
        await onUpdate({ positionDescription: null, positionDescriptionFileName: null });
      }
      setShowDeletePositionDescDialog(false);
      showToast.success('Job description deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete file:', error);
      showToast.error('Failed to delete job description');
      setPositionDescError(error.message || 'Failed to delete file');
    } finally {
      setIsDeletingPositionDesc(false);
    }
  };

  // --- Derived helpers ---

  const hasValidationErrors = Object.keys(dateErrors).length > 0;

  useEffect(() => {
    onValidationChange?.(hasValidationErrors);
  }, [hasValidationErrors, onValidationChange]);

  const resolveOrgUnitDisplay = (): string => {
    if (!employee.primaryOrgUnit && !employee.primaryOrgUnitName) return '-';
    if (employee.primaryOrgUnitName) return employee.primaryOrgUnitName;
    if (employee.org_path && employee.org_path.length > 0) {
      const lastNode = employee.org_path[employee.org_path.length - 1];
      if (lastNode?.name) return lastNode.name;
    }
    return employee.primaryOrgUnit || '-';
  };

  const resolveLocationDisplay = (): string => {
    const locVal = employee.location || employee.branch;
    console.log('[JobDetailsSection] Location Debug:', {
      locVal,
      employeeLocation: employee.location,
      employeeBranch: employee.branch,
      locationsCount: locations.length,
      locations: locations.map(l => ({ id: l.id, name: l.name })),
    });

    if (!locVal || locVal === '-' || locVal === '0') return '-';

    // If it's a number/ID, try to find name
    const matched = locations.find(loc => String(loc.id) === String(locVal));
    if (matched) {
      console.log('[JobDetailsSection] Location matched:', matched.name);
      return matched.name;
    }

    // If it looks like an ID (numeric) but no match found, and we have a branch name that isn't numeric...
    // But strictly, if we have a branch name, use it.
    if (employee.branch && !/^\d+$/.test(employee.branch)) {
      console.log('[JobDetailsSection] Using branch name:', employee.branch);
      return employee.branch;
    }

    console.log('[JobDetailsSection] Returning raw locVal:', locVal);
    return locVal;
  };

  return (
    <div className="w-full">
      {/* Header with Title and Inline Actions */}
      {!hideTitle && (
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900">Job Details</h3>
          {/* Only show edit controls when not externally controlled */}
          {!readOnly && externalIsEditing === undefined && (
            <div className="flex items-center gap-2">
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving || hasValidationErrors}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors ${isSaving || hasValidationErrors
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-50'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center gap-2 px-2 text-sm text-gray-600 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => {
                  setOriginalValues({
                    grade: employee.grade,
                    jobCode: employee.jobCode,
                    employee_id: employee.employee_id,
                    eeocCategory: employee.eeocCategory,
                    standardHours: employee.standardHours,
                    trainerContact: employee.trainerContact,
                    status: employee.status,
                    employmentType: employee.employmentType,
                    hireDateISO: employee.hireDateISO,
                    startDateISO: employee.startDateISO,
                    positionDateISO: employee.positionDateISO,
                    manager: employee.manager,
                    secondaryManager: employee.secondaryManager,
                    hrPartner: employee.hrPartner,
                    primaryOrgUnit: employee.primaryOrgUnit,
                    location: employee.location,
                    workRole: employee.workRole,
                    jobTitle: employee.jobTitle,
                    jobTitleId: employee.jobTitleId,
                  });
                  setIsEditing(true);
                }}>
                  Edit
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Content grid */}
      <div ref={formRef} className="relative grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8 w-full">

        {/* Title (editable dropdown) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Title</label>
          {isEditing ? (
            <div className="w-full">
              {primaryOrgUnitValue ? (
                <SmartJobTitleDropdown
                  scopeInstanceId={primaryOrgUnitValue}
                  selectedJobTitleId={jobTitleIdValue || null}
                  selectedJobTitleName={employee.jobTitle || (resolvedJobTitle !== '-' ? resolvedJobTitle : undefined)}
                  onSelectJobTitle={(jt) => {
                    setJobTitleIdValue(jt ? String(jt.job_title_id) : '');
                    setJobTitleNameValue(jt?.name ?? '');
                  }}
                  allowClear
                  placeholder="Select a job title"
                />
              ) : (
                <div className="w-full h-9 px-3 text-sm text-slate-400 bg-slate-50 border border-slate-200 rounded-md flex items-center">
                  Select an organizational unit first
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm font-medium text-slate-900 break-words py-2 min-h-[36px] flex items-center">
              {resolvedJobTitle}
            </div>
          )}
        </div>

        {/* Job Description (upload / download / delete) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4 min-w-0">
          <label className="text-sm font-medium text-slate-500">Job Description</label>
          <div className="flex flex-col gap-2 min-w-0 overflow-hidden">
            <div className="flex items-center gap-2 min-w-0">
              {employee.positionDescription ? (
                <>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-md min-w-0 flex-1 max-w-[280px]">
                    <div className="p-1 bg-blue-100 rounded shrink-0">
                      <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>
                    <span
                      className="text-sm font-medium text-slate-700 truncate"
                      title={employee.positionDescriptionFileName || 'Job Description'}
                    >
                      {employee.positionDescriptionFileName || 'Job Description'}
                    </span>
                  </div>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={handlePositionDescDownload}
                    disabled={isDownloadingPositionDesc}
                    title="Download document"
                    className="h-8 w-8 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                  >
                    {isDownloadingPositionDesc ? <Spinner size="xs" /> : <Download className="w-4 h-4" />}
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => setShowDeletePositionDescDialog(true)}
                    disabled={isDeletingPositionDesc}
                    title="Delete document"
                    className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50"
                  >
                    {isDeletingPositionDesc ? <Spinner size="xs" /> : <Trash2 className="w-4 h-4" />}
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handlePositionDescUpload}
                  disabled={isUploadingPositionDesc}
                  title="Upload job description"
                  className="h-8 text-xs px-3"
                >
                  {isUploadingPositionDesc ? (
                    <>
                      <Spinner size="xs" className="mr-1.5" />
                      Uploading...
                    </>
                  ) : (
                    'Choose file'
                  )}
                </Button>
              )}
            </div>
            {positionDescError && <p className="text-xs text-red-500">{positionDescError}</p>}
          </div>
        </div>

        {/* Job Grade (editable) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Job Grade</label>
          {isEditing ? (
            <input
              type="text"
              name="jobGrade"
              value={jobGradeValue}
              onChange={(e) => setJobGradeValue(e.target.value)}
              className="w-full px-3 py-2 text-sm text-slate-900 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter job grade"
            />
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {employee.grade || '-'}
            </div>
          )}
        </div>

        {/* Emp ID (editable with availability check) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Emp ID</label>
          {isEditing ? (
            <div className="relative">
              <input
                type="text"
                name="empId"
                value={empIdValue}
                onChange={(e) => setEmpIdValue(e.target.value)}
                className={`w-full px-3 py-2 text-sm text-slate-900 bg-white border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${employeeIdError ? 'border-red-500' : 'border-slate-200'
                  }`}
                placeholder="Enter employee ID"
              />
              {employeeIdError && <p className="text-xs text-red-600 mt-1">{employeeIdError}</p>}
              {isCheckingEmployeeId && !employeeIdError && (
                <p className="text-xs text-slate-400 mt-1">Checking...</p>
              )}
            </div>
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {employee.employee_id || '-'}
            </div>
          )}
        </div>

        {/* Job Code (editable) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Job Code</label>
          {isEditing ? (
            <input
              type="text"
              name="jobCode"
              value={jobCodeValue}
              onChange={(e) => setJobCodeValue(e.target.value)}
              className="w-full px-3 py-2 text-sm text-slate-900 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter job code"
            />
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {employee.jobCode || '-'}
            </div>
          )}
        </div>

        {/* EEOC Category (editable) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">EEOC Category</label>
          {isEditing ? (
            <Select value={eeocCategoryValue} onValueChange={setEeocCategoryValue}>
              <SelectTrigger className="w-full h-9 text-sm">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {eeocCategories.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {employee.eeocCategory || '-'}
            </div>
          )}
        </div>

        {/* Standard Hours (editable) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Standard Hours</label>
          {isEditing ? (
            <input
              type="number"
              name="standardHours"
              value={standardHoursValue}
              onChange={(e) => setStandardHoursValue(e.target.value)}
              className="w-full px-3 py-2 text-sm text-slate-900 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter standard hours"
            />
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {employee.standardHours || '40'}
            </div>
          )}
        </div>

        {/* Trainery Contact (editable) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Trainery Contact</label>
          {isEditing ? (
            <Select value={trainerContactValue} onValueChange={setTrainerContactValue}>
              <SelectTrigger className="w-full h-9 text-sm">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {traineryContacts.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {employee.trainerContact ? 'Yes' : 'No'}
            </div>
          )}
        </div>

        {/* Employment Status (editable) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Employment Status</label>
          {isEditing ? (
            <Select value={employmentStatusValue} onValueChange={setEmploymentStatusValue}>
              <SelectTrigger className="w-full h-9 text-sm">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {employmentStatuses.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {employee.status || 'Active'}
            </div>
          )}
        </div>

        {/* Employment Type (editable) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Employment Type</label>
          {isEditing ? (
            <Select value={employmentTypeValue} onValueChange={setEmploymentTypeValue}>
              <SelectTrigger className="w-full h-9 text-sm">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {employmentTypes.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {employee.employmentType || 'Full-Time'}
            </div>
          )}
        </div>

        {/* Hire Date (editable) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Hire Date</label>
          {isEditing ? (
            <DatePicker
              value={pendingDates.hireDate ?? (employee.hireDateISO || undefined)}
              onChange={(isoString) => handleDateChange('hireDate', isoString)}
              open={openCalendar === 'hire'}
              onOpenChange={(open) => setOpenCalendar(open ? 'hire' : null)}
              placeholder="Choose date"
              className="h-9 text-sm w-full"
            />
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {employee.hireDateISO ? DateTimeDisplay.toStandardDate(employee.hireDateISO) : '-'}
            </div>
          )}
        </div>

        {/* Start Date (editable, required) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">
            Start Date <span className="text-red-500">*</span>
          </label>
          {isEditing ? (
            <div className="w-full">
              <DatePicker
                value={pendingDates.startDate ?? (employee.startDateISO || undefined)}
                onChange={(isoString) => handleDateChange('startDate', isoString)}
                open={openCalendar === 'start'}
                onOpenChange={(open) => setOpenCalendar(open ? 'start' : null)}
                placeholder="Choose date"
                className={`h-9 text-sm w-full ${dateErrors.startDate ? 'border-red-500 focus:ring-red-500' : ''}`}
              />
              {dateErrors.startDate && <p className="mt-1 text-xs text-red-500">{dateErrors.startDate}</p>}
            </div>
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {employee.startDateISO ? DateTimeDisplay.toStandardDate(employee.startDateISO) : '-'}
            </div>
          )}
        </div>

        {/* Position Date (editable) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Position Date</label>
          {isEditing ? (
            <div className="w-full">
              <DatePicker
                value={pendingDates.positionDate ?? (employee.positionDateISO || undefined)}
                onChange={(isoString) => handleDateChange('positionDate', isoString)}
                open={openCalendar === 'position'}
                onOpenChange={(open) => setOpenCalendar(open ? 'position' : null)}
                placeholder="Choose date"
                className={`h-9 text-sm w-full ${dateErrors.positionDate ? 'border-red-500 focus:ring-red-500' : ''}`}
              />
              {dateErrors.positionDate && <p className="mt-1 text-xs text-red-500">{dateErrors.positionDate}</p>}
            </div>
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {employee.positionDateISO ? DateTimeDisplay.toStandardDate(employee.positionDateISO) : '-'}
            </div>
          )}
        </div>

        {/* Location (editable dropdown) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Location</label>
          {isEditing ? (
            <Select value={locationValue} onValueChange={(val: string) => setLocationValue(val)}>
              <SelectTrigger className="w-full h-9 text-sm">
                <SelectValue placeholder={isLoadingLocations ? 'Loading locations...' : locations.length === 0 ? 'No locations available' : 'Select location'} />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={String(loc.id)} value={String(loc.id)}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {resolveLocationDisplay()}
            </div>
          )}
        </div>

        {/* Work Role (editable dropdown) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Work Role</label>
          {isEditing ? (
            <Select value={workRoleValue} onValueChange={(val: string) => setWorkRoleValue(val)}>
              <SelectTrigger className="w-full h-9 text-sm">
                <SelectValue placeholder={isLoadingWorkRoles ? 'Loading work roles...' : workroles.length === 0 ? 'No work roles available' : 'Select work role'} />
              </SelectTrigger>
              <SelectContent>
                {workroles.map((role) => (
                  <SelectItem key={role.role_id} value={role.role_id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {(() => {
                console.log('[JobDetailsSection] Work Role Debug:', {
                  employeeWorkRole: employee.workRole,
                  workrolesCount: workroles.length,
                  workroles: workroles.map(r => ({ role_id: r.role_id, name: r.name })),
                });

                if (!employee.workRole) return '-';
                const role = workroles.find(r => r.role_id === employee.workRole || r.name === employee.workRole);

                if (role) {
                  console.log('[JobDetailsSection] Work Role matched:', role.name);
                  return role.name;
                }

                console.log('[JobDetailsSection] Work Role not matched, returning:', employee.workRole);
                return employee.workRole;
              })()}
            </div>
          )}
        </div>

        {/* Manager (editable) */}
        <div className="grid grid-cols-[140px_minmax(0,1fr)] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Manager</label>
          {isEditing ? (
            <div className="min-w-0">
              {primaryOrgUnitValue ? (
                <SmartManagerDropdown
                  scopeInstanceId={primaryOrgUnitValue}
                  employeeId={employee.id}
                  selectedManagerId={managerValue || null}
                  onSelectManager={(manager) => {
                    setManagerValue(manager?.user_uuid || '');
                  }}
                  allowClear={true}
                  className="w-full"
                />
              ) : (
                <div className="w-full h-10 px-4 text-sm text-slate-400 bg-slate-50 border border-slate-200 rounded-lg flex items-center">
                  Select an organizational unit first
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-2 items-center min-w-0 py-2 min-h-[36px]">
              {resolvedManagerName !== '-' && resolvedManagerAvatar && (
                <div className="shrink-0">
                  <img
                    src={resolvedManagerAvatar}
                    alt={resolvedManagerName}
                    className="w-5 h-5 rounded-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div className="w-5 h-5 bg-[#baedbd] rounded-full flex items-center justify-center hidden">
                    <span className="text-[10px] text-[#1c1c1c] font-normal">
                      {resolvedManagerName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              )}
              {resolvedManagerName !== '-' && !resolvedManagerAvatar && (
                <div className="shrink-0">
                  <div className="w-5 h-5 bg-[#baedbd] rounded-full flex items-center justify-center">
                    <span className="text-[10px] text-[#1c1c1c] font-normal">
                      {resolvedManagerName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              )}
              <div className="text-sm font-medium text-slate-900 truncate" title={resolvedManagerName}>
                {resolvedManagerName}
              </div>
            </div>
          )}
        </div>

        {/* Secondary Manager (editable) */}
        <div className="grid grid-cols-[140px_minmax(0,1fr)] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Secondary Manager</label>
          {isEditing ? (
            <div className="min-w-0">
              {primaryOrgUnitValue ? (
                <SmartManagerDropdown
                  scopeInstanceId={primaryOrgUnitValue}
                  employeeId={employee.id}
                  selectedManagerId={secondaryManagerValue || null}
                  onSelectManager={(manager) => {
                    setSecondaryManagerValue(manager?.user_uuid || '');
                  }}
                  allowClear={true}
                  className="w-full"
                />
              ) : (
                <div className="w-full h-10 px-4 text-sm text-slate-400 bg-slate-50 border border-slate-200 rounded-lg flex items-center">
                  Select an organizational unit first
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-2 items-center min-w-0 py-2 min-h-[36px]">
              {resolvedSecondaryManagerName !== '-' && resolvedSecondaryManagerAvatar && (
                <div className="shrink-0">
                  <img
                    src={resolvedSecondaryManagerAvatar}
                    alt={resolvedSecondaryManagerName}
                    className="w-5 h-5 rounded-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div className="w-5 h-5 bg-[#baedbd] rounded-full flex items-center justify-center hidden">
                    <span className="text-[10px] text-[#1c1c1c] font-normal">
                      {resolvedSecondaryManagerName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              )}
              {resolvedSecondaryManagerName !== '-' && !resolvedSecondaryManagerAvatar && (
                <div className="shrink-0">
                  <div className="w-5 h-5 bg-[#baedbd] rounded-full flex items-center justify-center">
                    <span className="text-[10px] text-[#1c1c1c] font-normal">
                      {resolvedSecondaryManagerName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              )}
              <div className="text-sm font-medium text-slate-900 truncate" title={resolvedSecondaryManagerName}>
                {resolvedSecondaryManagerName}
              </div>
            </div>
          )}
        </div>

        {/* HR Partner (editable) */}
        <div className="grid grid-cols-[140px_minmax(0,1fr)] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">HR Partner</label>
          {isEditing ? (
            <div className="min-w-0">
              {primaryOrgUnitValue ? (
                <SmartHRDropdown
                  scopeInstanceId={primaryOrgUnitValue}
                  employeeId={employee.id}
                  selectedHRPartnerId={hrPartnerValue || null}
                  onSelectHRPartner={(hrPartner) => {
                    setHrPartnerValue(hrPartner?.user_uuid || '');
                  }}
                  allowClear={true}
                  className="w-full"
                />
              ) : (
                <div className="w-full h-10 px-4 text-sm text-slate-400 bg-slate-50 border border-slate-200 rounded-lg flex items-center">
                  Select an organizational unit first
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-2 items-center min-w-0 py-2 min-h-[36px]">
              {resolvedHrPartnerName !== '-' && resolvedHrPartnerAvatar && (
                <div className="shrink-0">
                  <img
                    src={resolvedHrPartnerAvatar}
                    alt={resolvedHrPartnerName}
                    className="w-5 h-5 rounded-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const fallback = target.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                  <div className="w-5 h-5 bg-[#baedbd] rounded-full flex items-center justify-center hidden">
                    <span className="text-[10px] text-[#1c1c1c] font-normal">
                      {resolvedHrPartnerName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              )}
              {resolvedHrPartnerName !== '-' && !resolvedHrPartnerAvatar && (
                <div className="shrink-0">
                  <div className="w-5 h-5 bg-[#baedbd] rounded-full flex items-center justify-center">
                    <span className="text-[10px] text-[#1c1c1c] font-normal">
                      {resolvedHrPartnerName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
              )}
              <div className="text-sm font-medium text-slate-900 truncate" title={resolvedHrPartnerName}>
                {resolvedHrPartnerName}
              </div>
            </div>
          )}
        </div>

        {/* Unit (editable) */}
        <div className="grid grid-cols-[140px_1fr] items-center gap-4">
          <label className="text-sm font-medium text-slate-500">Unit</label>
          {isEditing ? (
            <div className="max-w-md">
              <OrgUnitSelector
                value={primaryOrgUnitValue}
                onChange={(val) => setPrimaryOrgUnitValue(val)}
                placeholder="Select unit"
              />
            </div>
          ) : (
            <div className="text-sm font-medium text-slate-900 py-2 min-h-[36px] flex items-center">
              {resolveOrgUnitDisplay()}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation dialog for internal save */}
      <ConfirmationDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={handleConfirmSave}
        title="Save Changes"
        description="Are you sure you want to save the changes to your job details?"
        confirmText="Save Changes"
        cancelText="Cancel"
        variant="info"
        isLoading={isSaving}
      />

      {/* Delete job description dialog */}
      <Dialog open={showDeletePositionDescDialog} onOpenChange={setShowDeletePositionDescDialog}>
        <DialogContent className="sm:max-w-md z-[9999]">
          <DialogHeader>
            <DialogTitle>Delete Job Description</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this job description document? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeletePositionDescDialog(false)}
              disabled={isDeletingPositionDesc}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handlePositionDescDelete} disabled={isDeletingPositionDesc}>
              {isDeletingPositionDesc ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Validation warning dialog */}
      <ConfirmationDialog
        open={validationWarning.show}
        onOpenChange={(open) => {
          if (!open) setValidationWarning({ show: false, message: '' });
        }}
        onConfirm={() => setValidationWarning({ show: false, message: '' })}
        title="Validation Error"
        description={validationWarning.message}
        confirmText="OK"
        variant="warning"
      />
    </div >
  );
};
